import React, { useState, useEffect } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import {
  Card,
  Form,
  Input,
  InputNumber,
  Select,
  Button,
  Space,
  Table,
  Row,
  Col,
  Divider,
  DatePicker,
  Typography,
  Spin,
} from "antd";
import {
  SaveOutlined,
  PlusOutlined,
  DeleteOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import {
  createGRN,
  updateGRN,
  getGRNById,
  getAllPurchaseOrders,
  getPurchaseOrderById,
} from "../../api/inventoryApi";
import PageTitle from "../page-header/PageHeader";
import checkTokenExp from "../../utils/checkTokenExp";

const { Text } = Typography;
const { TextArea } = Input;

const GRNForm = () => {
  // ── Auth ───────────────────────────────────────────────────
  const isLogged = Boolean(localStorage.getItem("isLogged"));
  const accessToken = localStorage.getItem("access-token");

  // ── Route ──────────────────────────────────────────────────
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id && id !== "new";

  // ── State ──────────────────────────────────────────────────
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [selectedPO, setSelectedPO] = useState(null);
  const [poLoading, setPOLoading] = useState(false);
  const [items, setItems] = useState([]);

  // ── Load purchase orders for dropdown ──────────────────────
  useEffect(() => {
    fetchPurchaseOrders();
    if (isEdit) {
      fetchGRN();
    }
  }, [id]);

  // ── Auth guard (after hooks) ───────────────────────────────
  if (!isLogged) {
    return <Navigate to={"/auth/login"} replace={true} />;
  }
  checkTokenExp(accessToken);

  const fetchPurchaseOrders = async () => {
    try {
      // Fetch POs that are sent/acknowledged/partially_received
      const result = await getAllPurchaseOrders({
        status: "sent,acknowledged,partially_received",
        count: 200,
      });
      setPurchaseOrders(result.data || []);
    } catch (error) {
      console.log(error.message);
    }
  };

  const fetchGRN = async () => {
    setLoading(true);
    try {
      const data = await getGRNById(id);
      form.setFieldsValue({
        purchase_order_id: data.purchase_order_id,
        invoice_number: data.invoice_number,
        invoice_date: data.invoice_date ? require("moment")(data.invoice_date) : null,
        vehicle_number: data.vehicle_number,
        lr_number: data.lr_number,
        notes: data.notes,
      });
      setItems(
        (data.items || []).map((item, idx) => ({
          key: idx,
          po_item_id: item.po_item_id,
          item_name: item.item_name || "",
          received_quantity: item.received_quantity || 0,
          accepted_quantity: item.accepted_quantity || 0,
          rejected_quantity: item.rejected_quantity || 0,
          qc_notes: item.qc_notes || "",
          storage_location: item.storage_location || "",
          batch_number: item.batch_number || "",
        }))
      );
    } catch (error) {
      toast.error("Failed to load GRN");
      navigate("/inventory/grn");
    } finally {
      setLoading(false);
    }
  };

  // ── When PO is selected, load its items ────────────────────
  const handlePOChange = async (poId) => {
    if (!poId) {
      setSelectedPO(null);
      setItems([]);
      return;
    }
    try {
      setPOLoading(true);
      const po = await getPurchaseOrderById(poId);
      setSelectedPO(po);
      // Pre-populate items from PO items that still have pending quantity
      const grnItems = (po.items || [])
        .filter((item) => (item.pending_quantity || item.quantity || 0) > 0)
        .map((item, idx) => ({
          key: idx,
          po_item_id: item.id,
          item_name: item.item_name || "",
          ordered_quantity: item.quantity || 0,
          pending_quantity: item.pending_quantity || item.quantity || 0,
          received_quantity: item.pending_quantity || item.quantity || 0,
          accepted_quantity: item.pending_quantity || item.quantity || 0,
          rejected_quantity: 0,
          qc_notes: "",
          storage_location: "",
          batch_number: "",
        }));
      setItems(grnItems);
    } catch (error) {
      toast.error("Failed to load PO details");
    } finally {
      setPOLoading(false);
    }
  };

  // ── Item table editing ─────────────────────────────────────
  const updateItem = (key, field, value) => {
    setItems((prev) =>
      prev.map((item) => {
        if (item.key !== key) return item;
        const updated = { ...item, [field]: value };
        // Auto-calc accepted when received changes
        if (field === "received_quantity") {
          updated.accepted_quantity = value - (updated.rejected_quantity || 0);
        }
        if (field === "rejected_quantity") {
          updated.accepted_quantity =
            (updated.received_quantity || 0) - (value || 0);
        }
        return updated;
      })
    );
  };

  const removeItem = (key) => {
    setItems((prev) => prev.filter((item) => item.key !== key));
  };

  // ── Submit ─────────────────────────────────────────────────
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (items.length === 0) {
        toast.error("Add at least one item");
        return;
      }

      setSubmitting(true);
      const payload = {
        purchase_order_id: values.purchase_order_id,
        invoice_number: values.invoice_number,
        invoice_date: values.invoice_date
          ? values.invoice_date.format("YYYY-MM-DD")
          : undefined,
        vehicle_number: values.vehicle_number,
        lr_number: values.lr_number,
        notes: values.notes,
        items: items.map((item) => ({
          po_item_id: item.po_item_id,
          received_quantity: item.received_quantity || 0,
          accepted_quantity: item.accepted_quantity || 0,
          rejected_quantity: item.rejected_quantity || 0,
          qc_notes: item.qc_notes || undefined,
          storage_location: item.storage_location || undefined,
          batch_number: item.batch_number || undefined,
          reel_width: item.reel_width || undefined,
        })),
      };

      if (isEdit) {
        await updateGRN(id, payload);
        toast.success("GRN updated");
      } else {
        await createGRN(payload);
        toast.success("GRN created");
      }
      navigate("/inventory/grn");
    } catch (error) {
      if (error.errorFields) return; // form validation error
      toast.error(
        error.response?.data?.message || "Failed to save GRN"
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ── Item columns ───────────────────────────────────────────
  const itemColumns = [
    {
      title: "Item",
      key: "item",
      width: 180,
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Text>{r.item_name || r.po_item_id}</Text>
          {r.pending_quantity !== undefined && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              Pending: {r.pending_quantity}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: "Received",
      key: "received",
      width: 110,
      render: (_, r) => (
        <InputNumber
          min={0}
          value={r.received_quantity}
          onChange={(v) => updateItem(r.key, "received_quantity", v || 0)}
          style={{ width: "100%" }}
          size="small"
        />
      ),
    },
    {
      title: "Accepted",
      key: "accepted",
      width: 110,
      render: (_, r) => (
        <InputNumber
          min={0}
          max={r.received_quantity}
          value={r.accepted_quantity}
          onChange={(v) => updateItem(r.key, "accepted_quantity", v || 0)}
          style={{ width: "100%" }}
          size="small"
        />
      ),
    },
    {
      title: "Rejected",
      key: "rejected",
      width: 110,
      render: (_, r) => (
        <InputNumber
          min={0}
          max={r.received_quantity}
          value={r.rejected_quantity}
          onChange={(v) => updateItem(r.key, "rejected_quantity", v || 0)}
          style={{ width: "100%" }}
          size="small"
        />
      ),
    },
    {
      title: "QC Notes",
      key: "qc_notes",
      render: (_, r) => (
        <Input
          value={r.qc_notes}
          onChange={(e) => updateItem(r.key, "qc_notes", e.target.value)}
          placeholder="QC notes..."
          size="small"
        />
      ),
    },
    {
      title: "Location",
      key: "location",
      width: 160,
      render: (_, r) => (
        <Input
          value={r.storage_location}
          onChange={(e) =>
            updateItem(r.key, "storage_location", e.target.value)
          }
          placeholder="e.g. Warehouse A"
          size="small"
        />
      ),
    },
    {
      title: "Batch #",
      key: "batch",
      width: 120,
      render: (_, r) => (
        <Input
          value={r.batch_number}
          onChange={(e) => updateItem(r.key, "batch_number", e.target.value)}
          placeholder="BATCH-001"
          size="small"
        />
      ),
    },
    {
      title: "Reel Width",
      key: "reel_width",
      width: 100,
      render: (_, r) => (
        r.category === "PAPER" ? (
          <InputNumber
            min={1}
            max={200}
            value={r.reel_width}
            onChange={(v) => updateItem(r.key, "reel_width", v)}
            placeholder="42"
            size="small"
            style={{ width: "100%" }}
            addonAfter='"'
          />
        ) : <Text type="secondary">—</Text>
      ),
    },
    {
      title: "",
      key: "actions",
      width: 50,
      render: (_, r) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => removeItem(r.key)}
          size="small"
        />
      ),
    },
  ];

  if (loading) {
    return (
      <>
        <PageTitle title="Back" subtitle="Loading GRN..." />
        <div style={{ textAlign: "center", padding: 80 }}>
          <Spin size="large" />
        </div>
      </>
    );
  }

  return (
    <>
      <PageTitle
        title="Back"
        subtitle={isEdit ? "Edit GRN" : "Create Goods Receipt Note"}
      />

      <Card
        title={
          <Space>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate("/inventory/grn")}
            />
            <span>{isEdit ? "Edit GRN" : "Create New GRN"}</span>
          </Space>
        }
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item
                name="purchase_order_id"
                label="Purchase Order"
                rules={[{ required: true, message: "Select a Purchase Order" }]}
              >
                <Select
                  showSearch
                  placeholder="Select PO"
                  loading={poLoading}
                  onChange={handlePOChange}
                  filterOption={(input, option) =>
                    (option?.children || "")
                      .toString()
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  disabled={isEdit}
                >
                  {purchaseOrders.map((po) => (
                    <Select.Option key={po.id} value={po.id}>
                      {po.po_number} — {po.supplier?.name || po.supplier_id || ""}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="invoice_number" label="Supplier Invoice #">
                <Input placeholder="e.g. INV-2026-001" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="invoice_date" label="Invoice Date">
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item name="vehicle_number" label="Vehicle Number">
                <Input placeholder="e.g. MH12AB1234" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="lr_number" label="LR Number">
                <Input placeholder="e.g. LR-5678" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="notes" label="Notes">
                <TextArea rows={1} placeholder="Any notes..." />
              </Form.Item>
            </Col>
          </Row>
        </Form>

        <Divider orientation="left">Items</Divider>

        {/* PO info banner */}
        {selectedPO && (
          <div
            style={{
              background: "#f6ffed",
              border: "1px solid #b7eb8f",
              borderRadius: 4,
              padding: "8px 12px",
              marginBottom: 16,
            }}
          >
            <Text>
              PO <strong>{selectedPO.po_number}</strong> • Supplier:{" "}
              {selectedPO.supplier?.name || selectedPO.supplier_id} • Total:{" "}
              ₹{selectedPO.total_amount?.toLocaleString() || 0}
            </Text>
          </div>
        )}

        <Table
          columns={itemColumns}
          dataSource={items}
          rowKey="key"
          pagination={false}
          size="small"
          scroll={{ x: 1000 }}
          locale={{
            emptyText: "Select a Purchase Order to load items",
          }}
        />

        <Divider />

        <Space>
          <Button onClick={() => navigate("/inventory/grn")}>Cancel</Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSubmit}
            loading={submitting}
            disabled={items.length === 0}
          >
            {isEdit ? "Update GRN" : "Create GRN"}
          </Button>
        </Space>
      </Card>
    </>
  );
};

export default GRNForm;
